import React from 'react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { FileText, ArrowLeft } from 'lucide-react';

const TermsOfService = () => {
  return (
    <div className="legal-page" data-testid="terms-of-service-page">
      <Helmet>
        <title>Terms of Service | The Gas Guide</title>
        <meta
          name="description"
          content="The terms governing use of The Gas Guide directory by homeowners and Gas Safe registered engineers."
        />
      </Helmet>

      {/* Hero */}
      <section className="legal-hero">
        <div className="legal-hero-content">
          <Link to="/" className="legal-back-link" data-testid="terms-back-link">
            <ArrowLeft size={16} /> Back to Home
          </Link>
          <span className="legal-label">
            <FileText size={14} /> Legal
          </span>
          <h1 className="legal-headline">Terms of Service</h1>
          <p className="legal-subline">
            The rules that govern use of The Gas Guide.
          </p>
          <p className="legal-meta">
            Effective date: <strong>2 March 2026</strong> &nbsp;·&nbsp; Last updated: 2 March 2026
          </p>
        </div>
      </section>

      {/* Content */}
      <section className="legal-body">
        <div className="legal-container">
          <div className="legal-intro">
            <p>
              These Terms of Service ("Terms") form a legal agreement between you and{' '}
              <strong>The Gas Guide</strong> ("we", "us", "our") and govern your access to and
              use of <a href="https://www.thegasguide.co.uk">www.thegasguide.co.uk</a> (the
              "Service"). By using the Service, you confirm that you accept these Terms.
            </p>
            <p>
              The Gas Guide is a UK-based directory connecting homeowners with vetted Gas Safe
              registered heating engineers. We do not employ engineers, do not perform gas work
              ourselves, and do not act as a contractor or agent for any party.
            </p>
          </div>

          <h2>1. Definitions</h2>
          <ul>
            <li>
              <strong>"Customer"</strong> — a homeowner or end-user using the Service to find an
              engineer.
            </li>
            <li>
              <strong>"Engineer"</strong> — a Gas Safe registered tradesperson with an active or
              trial listing on the directory.
            </li>
            <li>
              <strong>"Listing"</strong> — the public profile of an Engineer on the Service.
            </li>
            <li>
              <strong>"Subscription"</strong> — the recurring monthly fee paid by an Engineer for
              their Listing.
            </li>
          </ul>

          <h2>2. Eligibility</h2>
          <p>
            You must be at least 18 years old and a UK resident to use the Service. Engineers must
            hold a valid Gas Safe registration to apply for a Listing.
          </p>

          <h2>3. Our role</h2>
          <p>
            The Gas Guide is an introduction platform only. We verify each Engineer against the
            public Gas Safe Register at the time of approval, but we do not:
          </p>
          <ul>
            <li>guarantee the quality, timeliness or outcome of any work performed;</li>
            <li>act as a party to any contract between Customer and Engineer;</li>
            <li>
              hold or supervise funds paid between Customer and Engineer (any payments for work
              are made directly between the two parties).
            </li>
          </ul>
          <p>
            Any work undertaken by an Engineer is a private contract between the Customer and the
            Engineer.
          </p>

          <h2>4. Customer use</h2>
          <ul>
            <li>You agree to provide accurate postcode and contact information when searching.</li>
            <li>
              You agree to leave honest and lawful reviews. Reviews must not contain defamatory,
              false or abusive content.
            </li>
            <li>
              You must not use the Service to harvest engineer data, spam engineers or for any
              unlawful purpose.
            </li>
            <li>
              You acknowledge that we may moderate, edit or remove reviews that breach these
              Terms.
            </li>
          </ul>

          <h2>5. Engineer accounts &amp; listings</h2>
          <p>By creating an Engineer account, you confirm and agree that:</p>
          <ul>
            <li>
              All information you supply (name, Gas Safe number, photos, business details) is
              accurate and your own.
            </li>
            <li>
              You will keep your Gas Safe registration current and notify us immediately if it
              lapses.
            </li>
            <li>
              You will respond to customer enquiries professionally and in accordance with Gas
              Safe and applicable UK law.
            </li>
            <li>
              You are responsible for the quality, safety and warranty of any work you undertake.
            </li>
            <li>
              You will not misuse customer data obtained through the Service for unrelated
              marketing.
            </li>
            <li>
              We may suspend or terminate your Listing if you breach these Terms, lose Gas Safe
              registration, or receive serious or substantiated complaints.
            </li>
          </ul>

          <h3>5.1 Coverage area &amp; fairness</h3>
          <p>
            The directory is open to any Gas Safe registered engineer who passes our verification
            checks. We do not currently limit the number of Engineers in any given area —
            homeowners always benefit from more choice. We reserve the right to adjust coverage
            policies in future to keep the directory fair and high quality.
          </p>

          <h3>5.2 Founding Member status</h3>
          <p>
            "Founding Member" status is awarded to engineers who join the platform during our
            early growth phase. It is a non-transferable badge displayed on your public listing
            and may be removed if your listing is terminated.
          </p>

          <h2>6. Listings &amp; payment</h2>
          <ul>
            <li>
              <strong>Free listings:</strong> Listings are free for engineers. No card details are
              required at any point.
            </li>
          </ul>

          <h2>7. Reviews &amp; ratings</h2>
          <p>
            Reviews are written by Customers and reflect their personal experience. We may
            display a "Top Rated" badge on Engineer Listings that meet our published thresholds
            (currently a 4.7+ average from at least 3 reviews). Engineers may not solicit, buy,
            or fabricate reviews. We reserve the right to remove reviews and badges that breach
            these Terms.
          </p>

          <h2>8. Acceptable use</h2>
          <p>You must not:</p>
          <ul>
            <li>Use the Service to send unsolicited marketing, spam or scams.</li>
            <li>
              Attempt to bypass our security, scrape engineer or customer data, or reverse
              engineer the Service.
            </li>
            <li>Upload content that is unlawful, defamatory, or infringes third-party rights.</li>
            <li>Impersonate any person or misrepresent your trade qualifications.</li>
          </ul>
          <p>
            We may suspend or terminate accounts that breach this section without prior notice.
          </p>

          <h2>9. Intellectual property</h2>
          <p>
            All trademarks, logos, designs, copy and code on the Service belong to The Gas Guide
            or its licensors, except for content uploaded by Engineers (such as photos and
            descriptions). By uploading content, Engineers grant us a non-exclusive, royalty-free
            licence to display that content on the Service and in related marketing for the
            duration of their Listing.
          </p>

          <h2>10. Liability</h2>
          <p>
            To the fullest extent permitted by law, The Gas Guide is not liable for:
          </p>
          <ul>
            <li>
              the conduct, advice or workmanship of any Engineer found through the Service;
            </li>
            <li>
              indirect, incidental, special or consequential losses, including loss of profit,
              business or data;
            </li>
            <li>
              losses arising from third-party services (Stripe, postcodes.io, Google, OpenStreetMap)
              that we rely upon.
            </li>
          </ul>
          <p>
            Nothing in these Terms limits our liability for death or personal injury caused by
            negligence, fraud, or any other liability that cannot be excluded under UK law.
          </p>

          <h2>11. Indemnity</h2>
          <p>
            You agree to indemnify The Gas Guide against any claims, costs or losses arising from
            your breach of these Terms, your unlawful conduct, or (for Engineers) any work you
            perform for Customers introduced via the Service.
          </p>

          <h2>12. Termination</h2>
          <p>
            We may suspend or terminate any account at our reasonable discretion, including (but
            not limited to) if an Engineer loses Gas Safe registration, fails to pay a
            Subscription, or breaches these Terms. Customers may stop using the Service at any
            time.
          </p>

          <h2>13. Privacy</h2>
          <p>
            Use of the Service is also governed by our{' '}
            <Link to="/privacy" data-testid="terms-to-privacy-link">
              Privacy Policy
            </Link>
            , which forms part of these Terms.
          </p>

          <h2>14. Changes to these Terms</h2>
          <p>
            We may update these Terms from time to time. The "Last updated" date will reflect any
            changes. Material changes will be communicated by email or via a notice on the
            Service. Continued use after changes take effect constitutes acceptance of the
            updated Terms.
          </p>

          <h2>15. Governing law &amp; jurisdiction</h2>
          <p>
            These Terms are governed by the laws of England and Wales. Any disputes will be
            subject to the exclusive jurisdiction of the courts of England and Wales.
          </p>

          <h2>16. Contact</h2>
          <p className="legal-contact-block">
            <strong>The Gas Guide</strong>
            <br />
            Email: <a href="mailto:support@thegasguide.co.uk">support@thegasguide.co.uk</a>
            <br />
            Website:{' '}
            <a href="https://www.thegasguide.co.uk" target="_blank" rel="noopener noreferrer">
              www.thegasguide.co.uk
            </a>
          </p>

          <div className="legal-footer-links">
            <Link to="/privacy" data-testid="terms-to-privacy-link-bottom">
              Read our Privacy Policy →
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default TermsOfService;
